import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/** What a fact is about — mirrors FACT_TOPICS in the API's store-knowledge-contract.ts. */
export const STORE_FACT_TOPICS = [
    'showrooms', 'contact', 'installation', 'supplies', 'delivery', 'returns', 'samples', 'warranty', 'payment', 'care', 'other',
] as const;
export type StoreFactTopic = (typeof STORE_FACT_TOPICS)[number];

export type StoreKnowledgeStatus = 'never' | 'crawling' | 'ready' | 'empty' | 'failed' | 'blocked' | 'no_site';

export interface StoreFactView {
    id: string;
    topic: StoreFactTopic | string;
    field: string;
    label: string | null;
    value: string;
    quote: string | null;
    sourceUrl: string | null;
    /** `vendor` = written in the back office, not read off the site. */
    origin: 'crawl' | 'vendor';
    /** Edited (or removed) by the vendor: recrawls leave it alone. */
    locked: boolean;
    /** Removed by the vendor: never shown to shoppers. */
    hidden: boolean;
}

export interface StorePageView {
    url: string;
    topic: string;
    via: string | null;
    title: string | null;
    fetchedAt: string | null;
    error: string | null;
    httpStatus: number | null;
    facts: number;
}

export interface EscalationContact {
    phone?: string;
    email?: string;
    bookingUrl?: string;
    /** Where customers follow an order: the assistant sends order questions there. */
    ordersUrl?: string;
    note?: string;
}

export interface StoreKnowledgeView {
    enabled: boolean;
    website: string | null;
    status: StoreKnowledgeStatus;
    crawledAt: string | null;
    startedAt: string | null;
    error: string | null;
    nextCrawlAt: string | null;
    canRecrawlAt: string | null;
    pages: StorePageView[];
    facts: StoreFactView[];
    contact: EscalationContact | null;
    modelCalls: number;
    costUsd: number;
}

export interface StoreFactEdit {
    id?: string;
    topic?: string;
    field?: string;
    label?: string;
    value?: string;
    remove?: boolean;
}

/** What the card needs; the vendor and super-admin flavours differ only in where they send it. */
export interface StoreKnowledgeApi {
    load: () => Promise<StoreKnowledgeView>;
    recrawl: () => Promise<StoreKnowledgeView>;
    /** Absent where the viewer may not correct the facts. */
    editFact?: (edit: StoreFactEdit) => Promise<StoreKnowledgeView>;
    setContact?: (contact: EscalationContact) => Promise<StoreKnowledgeView>;
}

/** The signed-in vendor's own store knowledge. */
export const useStoreKnowledgeApi = (): StoreKnowledgeApi => {
    const { sendAuthGuardedRequest } = useAuthContext();
    return {
        load: async () => await sendAuthGuardedRequest(ApiMethod.GET, routes.storeKnowledge.view) as StoreKnowledgeView,
        recrawl: async () => await sendAuthGuardedRequest(ApiMethod.POST, routes.storeKnowledge.recrawl, {}) as StoreKnowledgeView,
        editFact: async (edit) => await sendAuthGuardedRequest(ApiMethod.PATCH, routes.storeKnowledge.facts, edit) as StoreKnowledgeView,
        setContact: async (contact) => await sendAuthGuardedRequest(ApiMethod.PUT, routes.storeKnowledge.contact, contact) as StoreKnowledgeView,
    };
};

/** Any vendor's store knowledge, for super admins: read and recrawl. */
export const useAdminStoreKnowledgeApi = (vendorId: string): StoreKnowledgeApi => {
    const { sendAuthGuardedRequest } = useAuthContext();
    return {
        load: async () => await sendAuthGuardedRequest(ApiMethod.GET, routes.storeKnowledge.adminView(vendorId)) as StoreKnowledgeView,
        recrawl: async () => await sendAuthGuardedRequest(ApiMethod.POST, routes.storeKnowledge.adminRecrawl(vendorId), {}) as StoreKnowledgeView,
    };
};
